import React, { useEffect, useId, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { ContentCopy, ContentCopyOutlined } from "@mui/icons-material";
import { swayyColors } from "../../../theme/tokens";

interface InlineCopyButtonProps {
  value: string;
  ariaLabel?: string;
  title?: string;
}

const COPY_SLOT_SIZE = 24;
/** Empty id clears every button's "copied" state. */
const CLEAR_COPY_FEEDBACK = "";

type CopyFeedbackListener = (activeId: string) => void;

const copyFeedbackListeners = new Set<CopyFeedbackListener>();

const notifyCopyFeedback = (activeId: string) => {
  copyFeedbackListeners.forEach((listener) => listener(activeId));
};

let documentListenerRefCount = 0;
let removeDocumentListeners: (() => void) | null = null;
/** Ignore the next native copy/cut after our own clipboard.writeText. */
let ignoreNextClipboardEvent = false;
/** Avoid clearing when focus/selection flickers during our button click. */
let suppressSelectionClearUntil = 0;

const ensureDocumentListeners = () => {
  documentListenerRefCount += 1;
  if (documentListenerRefCount > 1) return;

  const clearCopiedFeedback = () => {
    notifyCopyFeedback(CLEAR_COPY_FEEDBACK);
  };

  const onNativeCopyOrCut = () => {
    if (ignoreNextClipboardEvent) {
      ignoreNextClipboardEvent = false;
      return;
    }
    clearCopiedFeedback();
  };

  const onSelectionChange = () => {
    if (Date.now() < suppressSelectionClearUntil) return;
    const selection = document.getSelection();
    if (!selection || selection.isCollapsed) return;
    if (!selection.toString().trim()) return;
    clearCopiedFeedback();
  };

  document.addEventListener("copy", onNativeCopyOrCut, true);
  document.addEventListener("cut", onNativeCopyOrCut, true);
  document.addEventListener("selectionchange", onSelectionChange);

  removeDocumentListeners = () => {
    document.removeEventListener("copy", onNativeCopyOrCut, true);
    document.removeEventListener("cut", onNativeCopyOrCut, true);
    document.removeEventListener("selectionchange", onSelectionChange);
  };
};

const releaseDocumentListeners = () => {
  documentListenerRefCount = Math.max(0, documentListenerRefCount - 1);
  if (documentListenerRefCount === 0 && removeDocumentListeners) {
    removeDocumentListeners();
    removeDocumentListeners = null;
  }
};

const InlineCopyButton: React.FC<InlineCopyButtonProps> = ({
  value,
  ariaLabel = "Copy",
  title = "Copy",
}) => {
  const instanceId = useId();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onCopyFeedback = (activeId: string) => {
      setCopied(activeId === instanceId);
    };

    copyFeedbackListeners.add(onCopyFeedback);
    ensureDocumentListeners();

    return () => {
      copyFeedbackListeners.delete(onCopyFeedback);
      releaseDocumentListeners();
    };
  }, [instanceId]);

  const handleCopy = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!value) return;

    try {
      suppressSelectionClearUntil = Date.now() + 400;
      ignoreNextClipboardEvent = true;
      await navigator.clipboard.writeText(value);
      notifyCopyFeedback(instanceId);
    } catch (_error) {
      ignoreNextClipboardEvent = false;
    }
  };

  return (
    <Box
      sx={{
        width: COPY_SLOT_SIZE,
        minWidth: COPY_SLOT_SIZE,
        height: COPY_SLOT_SIZE,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        verticalAlign: "middle",
      }}
    >
      <IconButton
        size="small"
        title={copied ? "Copied" : title}
        aria-label={copied ? "Copied" : ariaLabel}
        onClick={handleCopy}
        sx={{
          color: copied ? swayyColors.pine : swayyColors.textSecondary,
          p: 0.35,
          width: COPY_SLOT_SIZE,
          height: COPY_SLOT_SIZE,
          "&:hover": {
            color: swayyColors.pine,
            bgcolor: swayyColors.pineLight,
          },
        }}
      >
        {copied ? (
          <ContentCopy sx={{ fontSize: 14 }} />
        ) : (
          <ContentCopyOutlined sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Box>
  );
};

export default InlineCopyButton;
